import { SANDBOX_PRESET_TAG as T } from '../presetTags'
import { SANDBOX_PRESET_BODY_CSS, type SandboxComponentPreset } from '../../sandboxPresetTypes'

/**
 * **Pointer capture** (`setPointerCapture`) keeps receiving `pointermove` after the cursor leaves the narrow gutter.
 * Grid: first track = left pane width (px), middle = gutter, last `1fr` eats remainder. On move, clamp px so panes stay usable.
 */
const shell = SANDBOX_PRESET_BODY_CSS

const preset: SandboxComponentPreset = {
  id: 'split-pane',
  category: 'drag-drop',
  title: 'Split pane',
  description: 'Draggable gutter resizes two columns (pointer events).',
  tags: [T.layout, T.resize],
  html: `<h2>Split</h2>

<div class="split" id="split">
  <div class="pane a">Left content (text will wrap).</div>
  <div class="gutter" id="gut" role="separator" aria-orientation="vertical" aria-label="Resize"></div>
  <div class="pane b">Right pane.</div>
</div>`,
  css: `${shell}

h2 {
  color: var(--nogrok-preview-primary);
  margin: 0 0 0.75rem;
}

/* Three columns: fluid | fixed gutter | fluid — JS overwrites first track to px */
.split {
  display: grid;
  grid-template-columns: 1fr 10px 1fr;
  max-width: 32rem;
  height: 140px;
  border: 1px solid var(--nogrok-preview-outline);
  border-radius: 8px;
  overflow: hidden;
}

.pane {
  padding: 0.75rem;
  font-size: 0.85rem;
  overflow: auto;
  background: var(--nogrok-preview-surface-container);
}

.gutter {
  background: var(--nogrok-preview-surface-container-high);
  cursor: col-resize;
}

.gutter:hover {
  background: color-mix(in srgb, var(--nogrok-preview-primary) 25%, transparent);
}`,
  js: `const sp = document.getElementById('split')
const gut = document.getElementById('gut')
let sx = 0
let sw = 0
let sl = 0
let drag = false

function leftColWidthPx() {
  if (!sp) return 0
  const leftPane = sp.querySelector('.pane.a')
  if (leftPane) return leftPane.getBoundingClientRect().width
  const first = sp.firstElementChild
  return first ? first.getBoundingClientRect().width : 0
}

gut?.addEventListener('pointerdown', (e) => {
  if (e.button !== 0) return
  drag = true
  sx = e.clientX
  if (sp) {
    sw = sp.getBoundingClientRect().width
    sl = leftColWidthPx()
  }
  try {
    gut.setPointerCapture(e.pointerId)
  } catch {
    /* ignore */
  }
})

gut?.addEventListener('pointermove', (e) => {
  if (!drag || !sp) return
  const dx = e.clientX - sx
  const p = Math.max(18, Math.min(sw - 28, sl + dx))
  sp.style.gridTemplateColumns = p + 'px 10px 1fr'
})

gut?.addEventListener('pointerup', () => {
  drag = false
})
gut?.addEventListener('pointercancel', () => {
  drag = false
})
gut?.addEventListener('lostpointercapture', () => {
  drag = false
})`,
}

export default preset
